import { useEffect, useState } from "react";
import { Check } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { SEM_FILA } from "../../constants/filas";

// Escolher as filas da lista de conversas.
//
// Era um popover que abria OUTRO popover dentro: o seletor de várias escolhas
// vivia espremido em 256px, as fichas das filas escolhidas quebravam em três
// linhas e a lista de opções aparecia por cima do próprio painel que a abriu.
// Num filtro que se usa o dia inteiro, isso é atrito toda vez.
//
// Aqui é uma lista só, com espaço para o nome inteiro e para a cor que
// identifica cada fila — e a escolha é aplicada no botão, não a cada clique:
// marcar quatro filas recarregava a lista quatro vezes.
const FiltroDeFilasModal = ({ aberto, aoFechar, filas, escolhidas, aoAplicar }) => {
	const [selecao, setSelecao] = useState(escolhidas || []);

	// Reabrir mostra o que está valendo agora, não o rascunho abandonado da
	// última vez que a pessoa desistiu no "Cancelar".
	useEffect(() => {
		if (aberto) setSelecao(escolhidas || []);
	}, [aberto, escolhidas]);

	const alternar = (id) =>
		setSelecao((atual) =>
			atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id],
		);

	// "Sem fila" fica no fim e separado: não é uma fila, é a ausência de uma.
	const opcoes = [
		...(filas || []).map((f) => ({
			id: f.id,
			nome: f.name,
			cor: f.color,
		})),
		{
			id: SEM_FILA,
			nome: "Sem fila",
			cor: "#94a3b8",
			ajuda: "Conversas que ainda não foram encaminhadas a um setor",
		},
	];

	const todasMarcadas = selecao.length === opcoes.length;

	return (
		<Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Filtrar por fila</DialogTitle>
					<DialogDescription>
						A lista de conversas mostra só as filas marcadas aqui.
					</DialogDescription>
				</DialogHeader>

				<div className="-mx-1 max-h-[50vh] overflow-y-auto px-1">
					{opcoes.map((o) => {
						const marcada = selecao.includes(o.id);

						return (
							<button
								key={o.id}
								type="button"
								onClick={() => alternar(o.id)}
								aria-pressed={marcada}
								className={cn(
									"flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors",
									marcada ? "bg-muted/60" : "hover:bg-muted/40",
									o.id === SEM_FILA && "mt-1 border-t pt-3",
								)}
							>
								<span
									className={cn(
										"flex h-4 w-4 shrink-0 items-center justify-center rounded border",
										marcada
											? "border-primary bg-primary text-primary-foreground"
											: "border-input",
									)}
								>
									{marcada && <Check className="h-3 w-3" />}
								</span>

								<span
									className="h-2.5 w-2.5 shrink-0 rounded-full"
									style={{ backgroundColor: o.cor }}
								/>

								<span className="min-w-0 flex-1">
									<span className="block truncate text-sm">{o.nome}</span>
									{o.ajuda && (
										<span className="block text-xs text-muted-foreground">
											{o.ajuda}
										</span>
									)}
								</span>
							</button>
						);
					})}
				</div>

				<DialogFooter className="sm:justify-between">
					{/* Um atalho só, que troca de sentido: com tudo marcado ele limpa,
					    e com algo desmarcado ele marca tudo. Dois botões ("Limpar" e
					    "Todas") ocupariam a barra inteira para a mesma decisão. */}
					<Button
						variant="ghost"
						onClick={() =>
							setSelecao(todasMarcadas ? [] : opcoes.map((o) => o.id))
						}
					>
						{todasMarcadas ? "Limpar" : "Marcar todas"}
					</Button>

					<div className="flex gap-2">
						<Button variant="outline" onClick={aoFechar}>
							Cancelar
						</Button>
						<Button
							onClick={() => {
								aoAplicar(selecao);
								aoFechar();
							}}
						>
							Aplicar
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default FiltroDeFilasModal;
